import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useProducts } from './ProductsContext';

export interface CartItem {
  slug: string;
  qty: number;
  addedAt: number;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (slug: string, qty?: number) => void;
  removeFromCart: (slug: string) => void;
  updateQty: (slug: string, qty: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  shippingCost: number;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
}

const STORAGE_KEY = 'rxk-cart';

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

function saveCart(cart: CartItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
}

function parsePrice(priceStr: string): number {
  return Number(priceStr.replace(/[^0-9]/g, ''));
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const { products, routineSet } = useProducts();
  const [cart, setCart] = useState<CartItem[]>(loadCart);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const getPriceBySlug = useCallback(
    (slug: string): number => {
      if (slug === '3-step-set') {
        return routineSet ? parsePrice(routineSet.price) : 0;
      }
      const p = products.find((x) => x.id === slug);
      return p ? parsePrice(p.price) : 0;
    },
    [products, routineSet],
  );

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  const addToCart = useCallback((slug: string, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.slug === slug);
      if (existing) {
        return prev.map((item) =>
          item.slug === slug ? { ...item, qty: item.qty + qty } : item,
        );
      }
      return [...prev, { slug, qty, addedAt: Date.now() }];
    });
  }, []);

  const removeFromCart = useCallback((slug: string) => {
    setCart((prev) => prev.filter((item) => item.slug !== slug));
  }, []);

  const updateQty = useCallback((slug: string, qty: number) => {
    if (qty <= 0) {
      setCart((prev) => prev.filter((item) => item.slug !== slug));
      return;
    }
    setCart((prev) =>
      prev.map((item) => (item.slug === slug ? { ...item, qty } : item)),
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const totalItems = useMemo(
    () => cart.reduce((sum, item) => sum + item.qty, 0),
    [cart],
  );

  const totalPrice = useMemo(
    () => cart.reduce((sum, item) => sum + getPriceBySlug(item.slug) * item.qty, 0),
    [cart, getPriceBySlug],
  );

  const shippingCost = useMemo(() => {
    if (totalPrice >= 3000) return 0;
    return 0; // Calculated at checkout based on city
  }, [totalPrice]);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQty,
        clearCart,
        totalItems,
        totalPrice,
        shippingCost,
        isCartOpen,
        openCart,
        closeCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}